/**
 * Boot-time PWA flag fetch (called by main.tsx before any SW work).
 *
 * Why fetch directly here (no React Query): the boot path runs BEFORE the
 * QueryClient mounts. We use `fetch` with a short timeout — if the API is
 * unreachable, every PWA feature reads as off (safe default).
 *
 * Result is intentionally NOT cached in module state: the admin /pwa toggle
 * needs to re-fetch this on every page boot (5s server cache + boot-only
 * fetch = fresh enough for an opt-in dormant feature).
 */
import {
  type PwaFlagSnapshot,
  PWA_FLAG_KEYS,
  PWA_FLAGS_ALL_OFF,
} from "./pwa-flags";

export const PUBLIC_FEATURE_FLAGS_URL = "/api/v2/public/feature-flags";
// Connected boot never blocks React rendering. Keeping this request alive
// avoids a slow current-authority response falling into the 30s refresh gap.
const DEFAULT_TIMEOUT_MS = 10_000;

interface RawFlagsEnvelope {
  readonly flags: Record<string, boolean>;
}

async function readFlagsEnvelope(
  response: Response,
): Promise<RawFlagsEnvelope> {
  return (await response.json()) as RawFlagsEnvelope;
}

/**
 * ONE boot fetch of the public feature-flags envelope (R17 review): the desktop
 * kill switch rides the SAME endpoint as the PWA flags. Fetching it twice raced
 * on the server's `Cache-Control: max-age=5` (the second fetch reused the
 * first's body, dropping whichever key the first handler omitted) — flaky in
 * e2e and wasteful. Callers derive both the PWA snapshot and the desktop flag
 * from this single raw map. Returns `null` on any non-ok/error/timeout so every
 * flag reads its safe default. Never throws.
 */
export async function fetchPublicFeatureFlagsRaw(
  fetchImpl: typeof fetch = fetch,
  url = PUBLIC_FEATURE_FLAGS_URL,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Record<string, boolean> | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(url, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return (await readFlagsEnvelope(response)).flags ?? {};
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Project a raw flags map onto the typed PWA snapshot (unknown keys dropped). */
export function mergePwaFlags(
  raw: Record<string, boolean> | null,
): PwaFlagSnapshot {
  if (raw === null) return PWA_FLAGS_ALL_OFF;
  const out: Record<string, boolean> = { ...PWA_FLAGS_ALL_OFF };
  for (const key of PWA_FLAG_KEYS) {
    if (typeof raw[key] === "boolean") out[key] = raw[key];
  }
  return out as unknown as PwaFlagSnapshot;
}

export async function fetchPwaFlagsOrAllOff(
  fetchImpl: typeof fetch = fetch,
  url = PUBLIC_FEATURE_FLAGS_URL,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<PwaFlagSnapshot> {
  return mergePwaFlags(
    await fetchPublicFeatureFlagsRaw(fetchImpl, url, timeoutMs),
  );
}
